"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Info as HelpCircle, PhoneCall, ShieldCheck, Clock, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "What are the clear signs my home needs a whole-house repiping?",
  answer: "Common signs include frequent pinhole leaks, rusty or discolored tap water, persistent low water pressure throughout the house, unusual noises in walls, and recurring plumbing repairs on galvanized steel or aged polybutylene pipes."
}, {
  id: "item-2",
  question: "How long does a typical repiping project take in Jonesboro?",
  answer: "Most residential repiping projects take between 2 to 4 days, depending on the square footage, number of bathrooms, and accessibility of plumbing runs. We keep water shutoffs limited strictly to working hours so your family has water each evening."
}, {
  id: "item-3",
  question: "Do we have to move out of our home during the repiping process?",
  answer: "No. You can stay comfortably in your home. Our licensed technicians protect flooring, furniture, and work areas with heavy drop cloths, clean up daily, and ensure primary water service is safely restored at the end of each workday."
}, {
  id: "item-4",
  question: "Which piping material is recommended: PEX or Copper?",
  answer: "Both have distinct advantages. PEX-A is flexible, highly resistant to freezing and acidic water corrosion, requires fewer drywall cuts, and is cost-effective. Copper offers rigid durability, natural antibacterial qualities, and long-standing longevity. We inspect your home layout and water chemistry to help you choose the best fit."
}, {
  id: "item-5",
  question: "Are estimates and pipe assessments free?",
  answer: "Yes, we provide comprehensive in-home pipe inspections and upfront flat-rate estimates across Jonesboro and surrounding areas. We explain all recommended scopes and financing options before any work begins."
}];
const guarantees = [{
  title: "Licensed & Insured",
  desc: "Certified Georgia master plumbers on every job."
}, {
  title: "Minimal Drywall Disruption",
  desc: "Precision wall cuts with complete clean-up daily."
}, {
  title: "Long-Term Warranty",
  desc: "Comprehensive workmanship and manufacturer coverage."
}];
export default function RepipingFAQ() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="repipingfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold text-secondary-foreground">
            <HelpCircle className="size-3.5 text-primary" />
            <span>Repiping Guidance</span>
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Frequently Asked Questions About Repiping
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Clear, honest answers about whole-home pipe replacement, timeline expectations, and material selections for homeowners in Jonesboro, GA.
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Accordion Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.55,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="border border-border shadow-md bg-card">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
                  {faqItems.map((item, i) => <div data-index={i} key={item.id}>
                      <AccordionItem value={item.id} className="border-border px-2 rounded-lg transition-colors">
                        <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-foreground hover:no-underline hover:text-primary py-4">
                          <span>{item.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4 pt-1">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    </div>)}
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>

          {/* Side Info & Direct CTA Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-6">
            {/* Consultation Card */}
            <Card className="bg-primary text-primary-foreground border-0 shadow-lg relative overflow-hidden">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-2 text-accent text-sm font-semibold mb-3 uppercase tracking-wider">
                  <Clock className="size-4" />
                  <span>24/7 Rapid Response</span>
                </div>
                <h3 className="text-2xl font-bold font-default text-primary-foreground mb-3">
                  Have an urgent pipe issue or need a repipe quote?
                </h3>
                <p className="text-primary-foreground/90 text-sm sm:text-base mb-6 leading-relaxed">
                  Speak directly with an experienced Jonesboro plumber to evaluate your water lines and get a transparent, itemized estimate.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="default" size="lg" asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-md w-full sm:w-auto">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call (470) 847-9076</span>
                    </a>
                  </Button>

                  <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground w-full sm:w-auto">
                    <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                      <span>Schedule Online</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Guarantees Box */}
            <Card className="border border-border bg-card shadow-sm">
              <CardContent className="p-6 sm:p-7">
                <h4 className="text-base font-semibold text-card-foreground mb-4 flex items-center gap-2">
                  <ShieldCheck className="size-5 text-primary" />
                  <span>Our Repiping Guarantee</span>
                </h4>
                <div className="space-y-3.5">
                  {guarantees.map((item, i) => <div data-index={i} key={item.title} className="flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {item.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}